// Gradixia — the shell every page sits in.

import { Suspense, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { rememberLang, SUPPORTED, type Lang } from "../lib/i18n.ts";
import { applyPageMeta, pageMeta } from "../lib/page-meta.ts";
import { useTheme } from "../lib/theme.ts";

/** Shown while a route's chunk arrives. Deliberately quiet: on a fast
    connection it is never seen, and a spinner that flashes for 80ms reads as a
    fault rather than as progress. */
function RouteLoading() {
  return (
    <p role="status" aria-live="polite"
       style={{ color: "var(--on-surface-3)", padding: "8px 0" }}>
      &nbsp;
    </p>
  );
}

/** The shell.
 *
 *  Normally renders whatever the router matched. `children` exists for
 *  build-time prerendering, which renders one known page into the same shell so
 *  the markup a crawler receives is the tree React later hydrates. */
export function Layout({ children }: { readonly children?: React.ReactNode } = {}) {
  const { t, i18n } = useTranslation();
  const current = (i18n.language === "en" ? "en" : "fr") as Lang;
  const { pathname } = useLocation();
  const { theme, effective, set } = useTheme();

  // The tab, the meta description and <html lang>, kept in step with BOTH the
  // route and the language. index.html carries one French title for the whole
  // application; without this an English reader keeps a French tab, and every
  // route announces itself as the landing page.
  useEffect(() => {
    if (typeof document === "undefined") return;
    applyPageMeta(document, pageMeta(t, pathname, current));
  }, [t, pathname, current, i18n.language]);

  const setLang = (l: Lang) => {
    void i18n.changeLanguage(l);
    document.documentElement.lang = l;
    rememberLang(l);
  };

  return (
    <>
      <a className="skip-link" href="#main">{t("common.skipToContent")}</a>

      <header className="shell-header">
        <div className="shell-header__inner">
          <Link to="/" className="shell-brand" aria-label="Gradixia">
            <img src="/gradixia-mark.png" alt="" width={36} height={36} style={{ display: "block" }} />
            <span>
              <span className="shell-brand__name" style={{ display: "block" }}>GRADIXIA</span>
              <span className="shell-brand__by" style={{ display: "block" }}>{t("brand.by")}</span>
            </span>
          </Link>

          <nav aria-label="Principal" className="shell-nav">
            <NavLink to="/diagnostic">{t("nav.diagnostic")}</NavLink>
            <NavLink to="/methode">{t("nav.method")}</NavLink>
            <NavLink to="/livrables">{t("nav.deliverables")}</NavLink>
            <NavLink to="/tarifs">{t("nav.pricing")}</NavLink>
            {/* The desk is the only route to a mandate, which is the largest
                thing the platform sells and the one the catalogue cannot price.
                It belongs in the header; /a-propos stays in the footer, where a
                reader goes after being interested rather than before. */}
            <NavLink to="/contact">{t("nav.contact")}</NavLink>
          </nav>

          <div className="shell-tools">
            <div role="group" aria-label="Langue" className="shell-lang">
              {SUPPORTED.map((l) => (
                <button key={l} type="button" onClick={() => setLang(l)} aria-pressed={current === l} lang={l}>
                  {l}
                </button>
              ))}
            </div>
            {/* Three choices the reader can see: light, dark, or the computer's
                own setting. Each is a pressed/unpressed button, so a screen
                reader hears "Sombre, pressed" rather than an icon. */}
            <div role="group" aria-label={t("theme.label")} className="shell-theme" data-effective={effective}>
              {(["light", "dark", "system"] as const).map((choice) => (
                <button key={choice} type="button" onClick={() => set(choice)} aria-pressed={theme === choice}
                        title={t(`theme.${choice}Title`)}>
                  <span aria-hidden="true">{choice === "light" ? "☼" : choice === "dark" ? "☾" : "◐"}</span>
                  <span className="sr-only">{t(`theme.${choice}`)}</span>
                </button>
              ))}
            </div>
            <Link to="/compte" className="shell-cta">{t("nav.account")}</Link>
          </div>
        </div>
      </header>

      <main id="main" style={{ maxWidth: "var(--max-width)", margin: "0 auto",
                               padding: "40px var(--gutter) 72px", minHeight: "60vh" }}>
        {/* One boundary for every routed page. Routes are code-split, so a
            first visit to the landing page no longer downloads the assessment
            runner, the operator console and the Stripe client along with it. */}
        <Suspense fallback={<RouteLoading />}>
          {children ?? <Outlet />}
        </Suspense>
      </main>

      <footer className="shell-footer">
        <div className="shell-footer__inner">
          <div>
            <div className="shell-footer__brand">
              <img src="/gradixia-mark.png" alt="" width={28} height={28} style={{ display: "block" }} />
              <span>GRADIXIA</span>
            </div>
            <p className="shell-footer__tag">{t("brand.tagline")}</p>
            <span className="shell-footer__engine">{t("brand.engine")}</span>
          </div>
          <nav aria-label={t("footer.product")}>
            <h2>{t("footer.product")}</h2>
            <ul>
              <li><Link to="/diagnostic">{t("nav.diagnostic")}</Link></li>
              <li><Link to="/methode">{t("nav.method")}</Link></li>
              <li><Link to="/tarifs">{t("nav.pricing")}</Link></li>
              <li><Link to="/livrables">{t("nav.deliverables")}</Link></li>
              <li><Link to="/verifier">{t("nav.verify")}</Link></li>
            </ul>
          </nav>
          <nav aria-label="Informations légales">
            <h2>{t("footer.legal")}</h2>
            <ul>
              <li><Link to="/a-propos">{t("nav.about")}</Link></li>
              <li><Link to="/contact">{t("nav.contact")}</Link></li>
              <li><Link to="/mentions-legales">{t("legal.mentions")}</Link></li>
              <li><Link to="/cgv">{t("legal.terms")}</Link></li>
              <li><Link to="/confidentialite">{t("legal.privacy")}</Link></li>
              <li><Link to="/compte">{t("nav.account")}</Link></li>
            </ul>
          </nav>
        </div>
        <div className="shell-footer__legal">
          <span>© {new Date().getFullYear()} Gradixia · Stredge Partners</span>
          <span>{t("footer.rights")}</span>
        </div>
      </footer>
    </>
  );
}
